import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, Stack, Typography } from "@mui/material";
import DataTablePage from "./DataTablePage";
import { fetchCustomers, setUserStatus } from "../../data/api";
import { useAuth } from "../../auth/AuthContext";

interface CustomerRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  status: string;
  accountType: string;
}

const statusColor = (s: string): "success" | "warning" | "default" | "error" => {
  const v = (s || "").toUpperCase();
  if (v === "APPROVED") return "success";
  if (v === "PENDING") return "warning";
  if (v === "SUSPENDED") return "error";
  return "default";
};

const CustomersPage: React.FC = () => {
  const { impersonating } = useAuth();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const users = (await fetchCustomers()) || [];
      // Backend already returns only this tenant's BUYER accounts; this is a
      // defensive guard so staff/admin rows can never render even if the API
      // contract regresses (QA #1: not frontend-filtering alone — see backend).
      const buyers = users.filter((u: any) => {
        const role = String(u?.accountType ?? u?.role ?? "").toUpperCase();
        return role === "" || role === "BUYER";
      });
      setRows(buyers.map((u: any) => ({
        id: Number(u?.id ?? 0),
        name: String(u?.name ?? "—"),
        email: String(u?.email ?? "—"),
        phone: String(u?.phone ?? "—"),
        status: String(u?.status ?? "—"),
        accountType: String(u?.accountType ?? u?.role ?? "—"),
      })));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id: number, status: string) => {
    setBusy(id);
    try { await setUserStatus(id, status); await load(); }
    finally { setBusy(null); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  const pending = rows.filter((r) => (r.status || "").toUpperCase() === "PENDING").length;

  return (
    <DataTablePage<CustomerRow>
      title="Customers"
      subtitle={`${rows.length} customers${pending ? ` · ${pending} awaiting approval` : ""}`}
      search={q} onSearch={setQ} searchPlaceholder="Search name or email"
      loading={loading} rows={filtered}
      empty="No customers yet. Buyers who register under your store appear here once created."
      columns={[
        { header: "Name", render: (r) => <Typography variant="body2" fontWeight={600}>{r.name}</Typography> },
        { header: "Email", render: (r) => r.email },
        { header: "Phone", render: (r) => r.phone },
        { header: "Account", render: (r) => r.accountType },
        { header: "Status", render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
        {
          header: "Actions",
          render: (r) => {
            const isDistributor = (r.accountType || "").toUpperCase() === "DISTRIBUTOR";
            if (isDistributor || !r.id) return <span>—</span>;
            const s = (r.status || "").toUpperCase();
            return (
              <Stack direction="row" spacing={1}>
                {s !== "APPROVED" && (
                  <Button size="small" variant="contained" disabled={busy === r.id || impersonating}
                    onClick={() => act(r.id, "APPROVED")}>Approve</Button>
                )}
                {s !== "SUSPENDED" && (
                  <Button size="small" variant="outlined" color="error" disabled={busy === r.id || impersonating}
                    onClick={() => act(r.id, "SUSPENDED")}>Suspend</Button>
                )}
              </Stack>
            );
          },
        },
      ]}
    />
  );
};
export default CustomersPage;
